import { useState, useCallback, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

const BASE = '/pricing-rules/margin-engine';

/**
 * The central Margin Engine, from the dashboard.
 *
 * No field mapping here, unlike the older pricing hooks: the API speaks the same
 * shape the screen edits, which is deliberate. Margin values are money, and a
 * mapping layer between the form and the server is one more place for a number to
 * change meaning on the way through.
 */

/** Product keys, in the order the spec lists them and the menu shows them. */
export const MARGIN_PRODUCTS = [
  { key: 'flightOnly', product: 'FLIGHT_ONLY', label: 'Flight Only', method: 'Fixed margin per traveller' },
  { key: 'hotelOnly', product: 'HOTEL_ONLY', label: 'Hotel Only', method: 'Supplier cost tiers + minimum' },
  { key: 'transferOnly', product: 'TRANSFER_ONLY', label: 'Transfer Only', method: 'Supplier cost tiers + minimum' },
  { key: 'dynamicPackage', product: 'DYNAMIC_PACKAGE', label: 'Dynamic Packaging', method: 'Base + variable % + minimum' },
  { key: 'cancellationInsurance', product: 'CANCELLATION_INSURANCE', label: 'Cancellation Insurance', method: '% of trip selling price + minimum' },
  { key: 'assistanceInsurance', product: 'ASSISTANCE_INSURANCE', label: 'Assistance Insurance', method: 'Per person per day' },
  { key: 'carRentalOnly', product: 'CAR_RENTAL_ONLY', label: 'Car Rental Only', method: 'Margin % + minimum' },
];

/** An empty section, so a config row that predates a product still renders. */
export const BLANK_SECTION = {
  flightOnly: { enabled: false, adt: 0, chd: 0, inf: 0 },
  hotelOnly: { enabled: false, tiers: [{ from: 0, to: null, percent: 0 }], minMargin: 0 },
  transferOnly: { enabled: false, tiers: [{ from: 0, to: null, percent: 0 }], minMargin: 0 },
  dynamicPackage: { enabled: false, baseMargin: 0, variablePercent: 0, minMargin: 0 },
  cancellationInsurance: { enabled: false, ratePercent: 0, minSellingPrice: 0 },
  assistanceInsurance: { enabled: false, pricePerPersonPerDay: 0 },
  carRentalOnly: { enabled: false, marginPercent: 0, minMargin: 0 },
};

const withDefaults = (cfg) => {
  const out = {};
  for (const { key } of MARGIN_PRODUCTS) {
    out[key] = { ...BLANK_SECTION[key], ...(cfg?.[key] || {}) };
    if ((key === 'hotelOnly' || key === 'transferOnly') && !Array.isArray(out[key].tiers)) {
      out[key].tiers = [...BLANK_SECTION[key].tiers];
    }
  }
  return out;
};

export const useMarginEngine = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.get(BASE);
      setConfig(withDefaults(data?.data?.marginEngine));
      return data?.data?.marginEngine ?? null;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load the margin configuration');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig().catch(() => {});
  }, [fetchConfig]);

  return { config, setConfig, loading, error, refetch: fetchConfig };
};

export const useSaveMarginEngine = () => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Save one or more sections.
   *
   * A 400 from the tier validator carries a list of field errors that say exactly
   * what is wrong with the table ("there is a gap between 99.99 and 200"). Those are
   * the whole point of the endpoint refusing, so they are surfaced rather than
   * flattened into "Validation failed".
   */
  const save = useCallback(async (patch) => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await axiosInstance.put(BASE, patch);
      return data?.data ?? null;
    } catch (err) {
      const list = err.response?.data?.errors;
      const msg = Array.isArray(list) && list.length
        ? list.map((e) => e.message).join(' ')
        : err.response?.data?.message || 'Failed to save the margin configuration';
      setError(msg);
      throw new Error(msg);
    } finally {
      setSaving(false);
    }
  }, []);

  return { save, saving, error };
};

/** Price a hypothetical booking against the SAVED config. Writes nothing. */
export const usePreviewMargin = () => {
  const [previewing, setPreviewing] = useState(false);

  const preview = useCallback(async (product, input) => {
    setPreviewing(true);
    try {
      const { data } = await axiosInstance.post(`${BASE}/preview`, { product, input });
      return data?.data ?? null;
    } catch (err) {
      return {
        ok: false,
        reason: err.response?.data?.message || 'PREVIEW_FAILED',
      };
    } finally {
      setPreviewing(false);
    }
  }, []);

  return { preview, previewing };
};

export default useMarginEngine;
